import * as React from "react";
import theme from "styles/theme";
import Confirm from "components/dialog/confirm";
import FormControl from "@material-ui/core/FormControl";
import Input from "@material-ui/core/Input";
import InputLabel from "@material-ui/core/InputLabel";
import InputAdornment from "@material-ui/core/InputAdornment";
import FormHelperText from "@material-ui/core/FormHelperText";
import { Form } from "formsy-react";
import { $, merge } from "glamor";
import Checkbox from "material-ui/Checkbox";
import { formatMessage } from "lib/globalize-util";
import { DATATEST } from "constants/integration";
import { ISizePacksTolerance, SizePacksToleranceThreshold } from "constants/product-collection";

const ABOVE_PLANNED = "abovePlanned";
const BELOW_PLANNED = "belowPlanned";

interface IProps {
    isSizeSettingsConfirmationOpen: boolean;
    onConfirm: (tolerance: ISizePacksTolerance | null) => void;
    onConfirmClose: () => void;
    confirmLabel?: string;
    onSuccess?: () => void;
    defaultSizePacksTolerance: ISizePacksTolerance | null;
}

export const SizeSettingsModal: React.FC<IProps> = ({
    onConfirm,
    isSizeSettingsConfirmationOpen,
    onConfirmClose,
    defaultSizePacksTolerance,
}) => {
    const [abovePlanned, setAbovePlanned] = React.useState<boolean>(
        [SizePacksToleranceThreshold.Both, SizePacksToleranceThreshold.Above].includes(
            defaultSizePacksTolerance?.direction
        )
    );
    const [belowPlanned, setBelowPlanned] = React.useState<boolean>(
        [SizePacksToleranceThreshold.Both, SizePacksToleranceThreshold.Below].includes(
            defaultSizePacksTolerance?.direction
        )
    );
    const [tolerance, setTolerance] = React.useState<number>(defaultSizePacksTolerance?.value ?? undefined);
    const styles = merge(
        $(".sizeContainer", {
            minHeight: 353,
        }),
        $(" .optionsCheck", {
            margin: "10px",
        }),
        $(" p.message", {
            padding: "20px",
        }),
        $(" .sizeSettingsTolerance", {
            padding: "0 20px",
        }),
        $(" .sizeSettingsTolerance .MuiInput-root", {
            border: "1px solid " + theme.palette.lightGrey,
            padding: "10px 16px",
            marginBottom: "0px",
            width: "200px",
        }),
        $(" .sizeSettingsTolerance label", {
            color: theme.palette.darkGrey,
            fontWeight: 400,
            fontSize: "1.5rem",
            left: "20px",
        }),
        $(" .sizeSettingsTolerance label + .MuiInput-formControl", {
            marginTop: "20px",
        })
    );
    const muiStyles = {
        titleContent: {
            display: "flex",
            alignItems: "center",
        },
    };
    const getSizeTolerance = (): ISizePacksTolerance | null => {
        if (!tolerance) {
            return null;
        }

        let direction = SizePacksToleranceThreshold.Both;
        if (abovePlanned && !belowPlanned) {
            direction = SizePacksToleranceThreshold.Above;
        } else if (!abovePlanned && belowPlanned) {
            direction = SizePacksToleranceThreshold.Below;
        }

        return {
            value: tolerance,
            direction,
        };
    };
    const handleToleranceChange = React.useCallback(
        (event: any) => {
            const value = event.currentTarget.value;
            setTolerance(value ? parseInt(value, 10) : undefined), [setTolerance];
        },
        [setTolerance]
    );
    const handleConfirm = () => {
        onConfirm(getSizeTolerance());
        onConfirmClose();
    };

    const handleConfirmClose = React.useCallback(
        (_e) => {
            onConfirmClose();
        },
        [onConfirmClose]
    );
    const isValidPercentage = tolerance !== undefined && tolerance > 0 && tolerance <= 100;

    const handleOptionschange = (e, status) => {
        const value = e.target.value;
        switch (value) {
            case ABOVE_PLANNED:
                setAbovePlanned(status);
                break;
            case BELOW_PLANNED:
                setBelowPlanned(status);
                break;
            default:
                break;
        }
    };

    return (
        <>
            <Confirm
                disabledConfirm={(!abovePlanned && !belowPlanned) || !isValidPercentage}
                contentStyle={{ maxWidth: "500px" }}
                title={
                    <div style={muiStyles.titleContent} data-test={DATATEST.ASSORTMENTS.TITLE_SIZE_SETTINGS_MODAL}>
                        <span>{formatMessage("Size Settings")}</span>
                    </div>
                }
                open={isSizeSettingsConfirmationOpen}
                onConfirm={handleConfirm}
                onCancel={handleConfirmClose}
                confirmLabel={formatMessage("Save")}
                cancelLabel={formatMessage("Cancel")}
            >
                <div className="sizeContainer" {...styles}>
                    <Form>
                        <div className="chooseDestinationContainer">
                            <p className="message">
                                {formatMessage(
                                    "Adjust your sizing parameters to be used with your size pack optimization logic. Keep in mind, if your tolerance is too low it may be difficult to find an optimal solution. We recommend at least 10%."
                                )}
                            </p>
                        </div>
                        <div>
                            <FormControl className="sizeSettingsTolerance" error={!isValidPercentage}>
                                <InputLabel shrink className="label">
                                    {formatMessage("Tolerance")}
                                </InputLabel>
                                <Input
                                    disableUnderline
                                    name="sizeSettingsTolerance"
                                    onChange={handleToleranceChange}
                                    value={tolerance ?? ""}
                                    type="number"
                                    endAdornment={<InputAdornment position="end">%</InputAdornment>}
                                    inputProps={{
                                        "data-test": DATATEST.ASSORTMENTS.SIZE_SETTINGS_MODAL.TOLERANCE_INPUT,
                                        min: 0,
                                        max: 100,
                                    }}
                                />
                                {!isValidPercentage && (
                                    <FormHelperText>
                                        {formatMessage("Must be a number between 0 and 100.")}
                                    </FormHelperText>
                                )}
                            </FormControl>
                        </div>
                        <p className="message">
                            <strong>{formatMessage("At least one of the following options is required:")} </strong>
                        </p>
                        <div className="optionsContainer">
                            <Checkbox
                                className="optionsCheck"
                                checked={abovePlanned}
                                value={ABOVE_PLANNED}
                                onCheck={handleOptionschange}
                                label={formatMessage("Above planned units")}
                                data-test={DATATEST.ASSORTMENTS.SIZE_SETTINGS_MODAL.ABOVE_PLANNED_CHECKBOX}
                            />
                            <Checkbox
                                className="optionsCheck"
                                checked={belowPlanned}
                                value={BELOW_PLANNED}
                                onCheck={handleOptionschange}
                                label={formatMessage("Below planned units")}
                                data-test={DATATEST.ASSORTMENTS.SIZE_SETTINGS_MODAL.BELOW_PLANNED_CHECKBOX}
                            />
                        </div>
                    </Form>
                </div>
            </Confirm>
        </>
    );
};
